import { assertUnreachable, OperationOk } from "@gnu-taler/taler-util";
import { useState } from "preact/hooks";

export type PaginationControl = {
  loadNext?(): void;
  loadPrev?(): void;
  loadFirst?(): void;
};
export type PaginatedResult<T> = OperationOk<T> & PaginationControl;

/**
 *
 * @param data the result of the requested list
 * @param offset offset id or index
 * @param setOffset function to be call on loadNext or loadFirst to specify the new offset
 * @param getId return the offset id of a row
 * @param PAGINATED_LIST_REQUEST the limit of the request, the UI is expted to show N -1 elements
 * @returns an OperationOk with two function. If the function is missing is because the offset is on the limit
 */
export function buildPaginatedResult<R>(
  data: R[],
  offset: ListPointer,
  setOffset: (o: R | undefined, direction: "asc" | "dec") => void,
  PAGINATED_LIST_REQUEST: number,
): PaginatedResult<R[]> {
  const {
    list: body,
    isLastPage,
    isFirstPage,
  } = __compute_for_ui(data, offset, PAGINATED_LIST_REQUEST);
  return {
    type: "ok",
    case: "ok",
    body,
    loadNext: isLastPage
      ? undefined
      : () => {
          if (!body.length) return;
          const id = body[body.length - 1];
          setOffset(id, "dec");
        },
    loadPrev: isFirstPage
      ? undefined
      : () => {
          if (!body.length) return;
          const id = body[0];
          setOffset(id, "asc");
        },
    loadFirst: isFirstPage
      ? undefined
      : () => {
          setOffset(undefined, "dec");
        },
  };
}

/**
 * based on the pointer and the max result set
 *
 * return relative pos and list to show
 * @param data
 * @param offset
 * @param max
 * @returns
 */
function __compute_for_ui<R>(data: Array<R>, offset: ListPointer, max: number) {
  // we ask for N but show N-1
  // the last element on the list is to signal if
  // we have more pages after it
  const thereIsMore = data.length == max;
  const result: R[] = structuredClone(data);
  if (thereIsMore) {
    result.pop();
  }
  // we assume the UI always show on DEC order
  // so what the offset.order is telling is
  // in which direction the last request was.
  switch (offset.order) {
    case "asc": {
      const isFirstPage = !thereIsMore;
      const isLastPage = offset.id === undefined;
      const list = result.reverse();
      return { list, isFirstPage, isLastPage };
    }
    case "dec": {
      const isLastPage = !thereIsMore;
      const isFirstPage = offset.id === undefined;
      const list = result;
      return { list, isFirstPage, isLastPage };
    }
    default: {
      assertUnreachable(offset.order);
    }
  }
}

/**
 * Define the entry point of a list when asking for a resultset.
 *
 * If id is not set then assume the top most entry point.
 */
export type ListPointer = { id?: string; order: "asc" | "dec" };

/**
 *
 * @param getId given a row type T return the rowId
 * @returns current pointer and a function to move
 */
export function useListPointer<T>(
  getId: (d: T) => string,
): [ListPointer, (p: T | undefined, order: ListPointer["order"]) => void] {
  const initial = {
    order: "dec" as const,
  };
  const [pointer, setPointer] = useState<ListPointer>(initial);
  return [
    pointer,
    (d, order) => {
      if (!d) {
        setPointer(initial);
      } else {
        setPointer({
          order,
          id: getId(d),
        });
      }
    },
  ];
}
